// src/firebase/competitions.ts
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "./config";
import { deleteRegistrationsByCompetition } from "./registrations";
import { withCache, invalidateCache, invalidateCachePrefix } from "../utils/cache";

const COLLECTION = "competitions";

// ── Cache TTL ─────────────────────────────────────────────────────────────────
// Competitions rarely change — they're created once and edited maybe twice
// per event. 15 minutes is the right TTL; every write below invalidates the
// cache so admin edits show up immediately.
const TTL_ALL_COMPETITIONS = 15 * 60 * 1000; // 15 minutes
const TTL_SUMMARY          = 15 * 60 * 1000; // matches; lean variant for public pages

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

/**
 * Returns all competitions ordered by startDate descending.
 * Cached in-memory + persisted to localStorage (Timestamps survive the
 * JSON round-trip via the cache's Timestamp serializer).
 */
export const getAllCompetitions = async () => {
  return withCache(
    "competitions:all",
    TTL_ALL_COMPETITIONS,
    async () => {
      const q = query(collection(db, COLLECTION), orderBy("startDate", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
    { persist: true }
  );
};

/**
 * Lean summary list for landing / public pages. Drops the heavy fields
 * (weightCategories, full price tiers beyond the headline `latePrice`,
 * ranking config) so the on-the-wire payload stays small even at hundreds
 * of competitions. Pages that need the full doc (e.g. registration form)
 * call getAllCompetitions() separately.
 */
export interface CompetitionSummary {
  id: string;
  name: string;
  status?: string;
  imageUrl?: string;
  types?: string[];
  startDate: any;
  endDate: any;
  registrationDeadline: any;
  earlyDeadline?: any;
  midDeadline?: any;
  /** Coach-side cutoff for weight/division edits. Falls back to startDate when absent. */
  weightChangeDeadline?: any;
  earlyPrice?: number;
  midPrice?: number;
  latePrice?: number;
  registrationCount?: number;
  /** Official regulations/provision PDF (Firebase Storage URL). Publicly readable. */
  documentUrl?: string;
  /** Original filename of the official document, used as the public link label. */
  documentName?: string;
  /** Second, optional document (e.g. English version). Firebase Storage URL, publicly readable. */
  documentUrl2?: string;
  /** Original filename of the second optional document. */
  documentName2?: string;
}

export const getCompetitionsSummary = async (): Promise<CompetitionSummary[]> => {
  return withCache(
    "competitions:summary",
    TTL_SUMMARY,
    async () => {
      const q = query(collection(db, COLLECTION), orderBy("startDate", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          name: data.name,
          status: data.status,
          imageUrl: data.imageUrl,
          types: data.types,
          startDate:            data.startDate,
          endDate:              data.endDate,
          registrationDeadline: data.registrationDeadline,
          earlyDeadline:        data.earlyDeadline,
          midDeadline:          data.midDeadline,
          weightChangeDeadline: data.weightChangeDeadline,
          earlyPrice: data.earlyPrice,
          midPrice:   data.midPrice,
          latePrice:  data.latePrice,
          registrationCount: data.registrationCount,
          documentUrl:  data.documentUrl,
          documentName: data.documentName,
          documentUrl2:  data.documentUrl2,
          documentName2: data.documentName2,
        };
      });
    },
    { persist: true }
  );
};

// ---------------------------------------------------------------------------
// CREATE
// Accepts an optional imageFile — if provided, uploads it after doc creation.
// ---------------------------------------------------------------------------

export const addCompetition = async (data: any, imageFile?: File) => {
  const { weightChangeDeadline: wcd, ...rest } = data;
  const docRef = await addDoc(collection(db, COLLECTION), {
    ...rest,
    imageUrl: "",
    startDate:            Timestamp.fromDate(new Date(data.startDate)),
    endDate:              Timestamp.fromDate(new Date(data.endDate)),
    registrationDeadline: Timestamp.fromDate(new Date(data.registrationDeadline)),
    earlyDeadline:        Timestamp.fromDate(new Date(data.earlyDeadline)),
    midDeadline:          Timestamp.fromDate(new Date(data.midDeadline)),
    // Optional — omitted entirely when blank so the registrations helper can fall back to startDate
    ...(wcd ? { weightChangeDeadline: Timestamp.fromDate(new Date(wcd)) } : {}),
  });

  if (imageFile) {
    const storageRef = ref(
      storage,
      `competition-images/${docRef.id}/${imageFile.name}`
    );
    await uploadBytes(storageRef, imageFile, { cacheControl: "public, max-age=2592000, immutable" });
    const downloadUrl = await getDownloadURL(storageRef);
    await updateDoc(doc(db, COLLECTION, docRef.id), { imageUrl: downloadUrl });
  }

  invalidateCachePrefix("competitions:");
  return docRef;
};

// ---------------------------------------------------------------------------
// UPDATE — status
// ---------------------------------------------------------------------------

export const updateCompetitionStatus = async (docId: string, status: string) => {
  const result = await updateDoc(doc(db, COLLECTION, docId), { status });
  invalidateCachePrefix("competitions:");
  return result;
};

// ---------------------------------------------------------------------------
// UPDATE — full competition document (all fields)
// ---------------------------------------------------------------------------

export const updateCompetition = async (
  competitionId: string,
  data: any,
  imageFile?: File
): Promise<void> => {
  const { weightChangeDeadline: wcd, ...rest } = data;
  const updateData: any = {
    ...rest,
    startDate:            Timestamp.fromDate(new Date(data.startDate)),
    endDate:              Timestamp.fromDate(new Date(data.endDate)),
    registrationDeadline: Timestamp.fromDate(new Date(data.registrationDeadline)),
    earlyDeadline:        Timestamp.fromDate(new Date(data.earlyDeadline)),
    midDeadline:          Timestamp.fromDate(new Date(data.midDeadline)),
    // Optional — when cleared in the form we leave the field untouched on
    // the doc so callers can still rely on the previously-stored value or
    // the startDate fallback.
    ...(wcd ? { weightChangeDeadline: Timestamp.fromDate(new Date(wcd)) } : {}),
  };

  await updateDoc(doc(db, COLLECTION, competitionId), updateData);

  if (imageFile) {
    const storageRef = ref(storage, `competition-images/${competitionId}/${imageFile.name}`);
    await uploadBytes(storageRef, imageFile, { cacheControl: "public, max-age=2592000, immutable" });
    const downloadUrl = await getDownloadURL(storageRef);
    await updateDoc(doc(db, COLLECTION, competitionId), { imageUrl: downloadUrl });
  }

  invalidateCachePrefix("competitions:");
};

// ---------------------------------------------------------------------------
// UPDATE — competition image (Firebase Storage)
// ---------------------------------------------------------------------------

export const updateCompetitionImage = async (
  competitionId: string,
  file: File,
  existingImageUrl?: string
): Promise<string> => {
  if (existingImageUrl) {
    try {
      const oldRef = ref(storage, existingImageUrl);
      await deleteObject(oldRef);
    } catch {
      // Old file may already be gone — safe to ignore
    }
  }

  const storageRef = ref(storage, `competition-images/${competitionId}/${file.name}`);
  await uploadBytes(storageRef, file, { cacheControl: "public, max-age=2592000, immutable" });
  const downloadUrl = await getDownloadURL(storageRef);

  await updateDoc(doc(db, COLLECTION, competitionId), { imageUrl: downloadUrl });

  invalidateCachePrefix("competitions:");
  return downloadUrl;
};

// ---------------------------------------------------------------------------
// UPDATE — official document PDF (Firebase Storage)
//   The official "provision / regulations" PDF for a competition. Stored on a
//   dedicated competition-documents/{id}/ path so its Storage permissions are
//   independent of the (admin-only) competition images — the Storage rule for
//   this path grants public read + write to admin AND ranking_admin. We keep
//   both the download URL and the original filename so the public link can
//   show a real label. Editable / removable any time by admin or ranking_admin.
// ---------------------------------------------------------------------------

export const updateCompetitionDocument = async (
  competitionId: string,
  file: File,
  existingDocUrl?: string
): Promise<string> => {
  // Remove the previous document first so Storage doesn't accumulate orphans.
  if (existingDocUrl) {
    try {
      await deleteObject(ref(storage, existingDocUrl));
    } catch {
      // Old file may already be gone — safe to ignore.
    }
  }

  const storageRef = ref(storage, `competition-documents/${competitionId}/${file.name}`);
  await uploadBytes(storageRef, file, { contentType: "application/pdf", cacheControl: "public, max-age=86400" });
  const downloadUrl = await getDownloadURL(storageRef);

  await updateDoc(doc(db, COLLECTION, competitionId), {
    documentUrl: downloadUrl,
    documentName: file.name,
  });

  invalidateCachePrefix("competitions:");
  return downloadUrl;
};

// ---------------------------------------------------------------------------
// REMOVE — official document PDF (clears the fields + deletes the file)
// ---------------------------------------------------------------------------

export const removeCompetitionDocument = async (
  competitionId: string,
  existingDocUrl?: string
): Promise<void> => {
  if (existingDocUrl) {
    try {
      await deleteObject(ref(storage, existingDocUrl));
    } catch {
      // Already gone — ignore.
    }
  }

  await updateDoc(doc(db, COLLECTION, competitionId), {
    documentUrl: "",
    documentName: "",
  });

  invalidateCachePrefix("competitions:");
};

// ---------------------------------------------------------------------------
// UPDATE — second optional document PDF (e.g. English version)
//   Stored in the SAME folder as the primary document so it stays within the
//   existing Storage rule depth, but with a fixed "secondary-" object-name
//   prefix so it can never collide with documentUrl even if the coach picks a
//   file with the same name. Writes documentUrl2 / documentName2 and leaves
//   documentUrl / documentName untouched.
// ---------------------------------------------------------------------------

export const updateCompetitionDocument2 = async (
  competitionId: string,
  file: File,
  existingDoc2Url?: string
): Promise<string> => {
  // Remove the previous second document first so Storage doesn't accumulate orphans.
  if (existingDoc2Url) {
    try {
      await deleteObject(ref(storage, existingDoc2Url));
    } catch {
      // Old file may already be gone — safe to ignore.
    }
  }

  const storageRef = ref(
    storage,
    `competition-documents/${competitionId}/secondary-${file.name}`
  );
  await uploadBytes(storageRef, file, { contentType: "application/pdf", cacheControl: "public, max-age=86400" });
  const downloadUrl = await getDownloadURL(storageRef);

  await updateDoc(doc(db, COLLECTION, competitionId), {
    documentUrl2: downloadUrl,
    documentName2: file.name,
  });

  invalidateCachePrefix("competitions:");
  return downloadUrl;
};

// ---------------------------------------------------------------------------
// REMOVE — second optional document PDF (clears the fields + deletes the file)
// ---------------------------------------------------------------------------

export const removeCompetitionDocument2 = async (
  competitionId: string,
  existingDoc2Url?: string
): Promise<void> => {
  if (existingDoc2Url) {
    try {
      await deleteObject(ref(storage, existingDoc2Url));
    } catch {
      // Already gone — ignore.
    }
  }

  await updateDoc(doc(db, COLLECTION, competitionId), {
    documentUrl2: "",
    documentName2: "",
  });

  invalidateCachePrefix("competitions:");
};

// ---------------------------------------------------------------------------
// DELETE — competition document, its image + PDF(s), and all its registrations
// ---------------------------------------------------------------------------

export const deleteCompetition = async (
  competitionId: string,
  existingImageUrl?: string,
  existingDocUrl?: string,
  existingDoc2Url?: string
): Promise<void> => {
  if (existingImageUrl) {
    try {
      const imageRef = ref(storage, existingImageUrl);
      await deleteObject(imageRef);
    } catch {
      // Safe to ignore if file doesn't exist
    }
  }

  if (existingDocUrl) {
    try {
      await deleteObject(ref(storage, existingDocUrl));
    } catch {
      // Safe to ignore if file doesn't exist
    }
  }

  if (existingDoc2Url) {
    try {
      await deleteObject(ref(storage, existingDoc2Url));
    } catch {
      // Safe to ignore if file doesn't exist
    }
  }

  await deleteRegistrationsByCompetition(competitionId);
  await deleteDoc(doc(db, COLLECTION, competitionId));

  invalidateCachePrefix("competitions:");
};

// ---------------------------------------------------------------------------
// RENAME PROPAGATION
// ---------------------------------------------------------------------------

/**
 * Propagates a competition name/date change to all dependent collections:
 *   - rankingResults: competitionName, year, month
 *   - registrations: competitionName
 *   - rankingCompetitions: name, nameKa, year, month (via linkedCompetitionId)
 *
 * Does NOT import from rankings.ts to avoid circular dependencies.
 * Safe to re-run. Returns counts and whether rankingCompetitions was updated.
 *
 * rankingResults are keyed by the rankingCompetitions doc ID (NOT the main
 * competition ID). This function therefore:
 *   1. Resolves the linked rankingCompetitions doc via linkedCompetitionId.
 *   2. Uses that doc's ID to query / update rankingResults.
 *   3. If no linked rankingCompetitions doc exists, skips rankingResults
 *      entirely and returns rankingResultsUpdated: 0.
 */
export const propagateCompetitionRename = async (
  competitionId: string,
  updates: {
    newName?: string;
    newNameKa?: string;
    newStartDate?: Date;
  }
): Promise<{
  rankingResultsUpdated: number;
  registrationsUpdated: number;
  rankingCompetitionUpdated: boolean;
}> => {
  const CHUNK = 499;

  // 1. Find the linked rankingCompetitions document (if any).
  //    This is the single source of truth for the ranking competition ID.
  const rcSnap = await getDocs(
    query(
      collection(db, "rankingCompetitions"),
      where("linkedCompetitionId", "==", competitionId)
    )
  );

  // 2. Resolve the ranking competition ID.
  //    If no linked record exists, rankingResults cannot be updated — there is
  //    no valid competitionId to query them with. Return 0 for that counter.
  const rankingCompId: string | null = rcSnap.empty ? null : rcSnap.docs[0].id;

  // 3. Query rankingResults only when a linked record was found.
  //    rankingResults.competitionId === rankingCompetitions doc ID, NOT the
  //    main competition ID — this is why the fallback was wrong.
  const rrSnap = rankingCompId
    ? await getDocs(
        query(
          collection(db, "rankingResults"),
          where("competitionId", "==", rankingCompId)
        )
      )
    : null;

  // 4. Query registrations (always keyed by the main competition ID).
  const regSnap = await getDocs(
    query(
      collection(db, "registrations"),
      where("competitionId", "==", competitionId)
    )
  );

  // 5. Build rankingResults update payload.
  const rrUpdate: Record<string, any> = {};
  if (updates.newName) rrUpdate.competitionName = updates.newName;
  if (updates.newStartDate) {
    rrUpdate.year  = updates.newStartDate.getFullYear();
    rrUpdate.month = updates.newStartDate.getMonth() + 1;
  }

  // 6. Batch-update rankingResults.
  const rrDocs = rrSnap ? rrSnap.docs : [];
  if (rrDocs.length > 0 && Object.keys(rrUpdate).length > 0) {
    for (let i = 0; i < rrDocs.length; i += CHUNK) {
      const batch = writeBatch(db);
      rrDocs.slice(i, i + CHUNK).forEach((d) => batch.update(d.ref, rrUpdate));
      await batch.commit();
    }
  }

  // 7. Build registrations update payload.
  const regUpdate: Record<string, any> = {};
  if (updates.newName) regUpdate.competitionName = updates.newName;

  // Batch-update registrations.
  const regDocs = regSnap.docs;
  if (regDocs.length > 0 && Object.keys(regUpdate).length > 0) {
    for (let i = 0; i < regDocs.length; i += CHUNK) {
      const batch = writeBatch(db);
      regDocs.slice(i, i + CHUNK).forEach((d) => batch.update(d.ref, regUpdate));
      await batch.commit();
    }
  }

  // 8. Update the linked rankingCompetitions document itself.
  let rankingCompetitionUpdated = false;
  if (!rcSnap.empty) {
    const rcDoc = rcSnap.docs[0];
    const rcUpdate: Record<string, any> = {};
    if (updates.newName)     rcUpdate.name    = updates.newName;
    if (updates.newNameKa)   rcUpdate.nameKa  = updates.newNameKa;
    if (updates.newStartDate) {
      rcUpdate.year  = updates.newStartDate.getFullYear();
      rcUpdate.month = updates.newStartDate.getMonth() + 1;
    }
    if (Object.keys(rcUpdate).length > 0) {
      await updateDoc(rcDoc.ref, rcUpdate);
      rankingCompetitionUpdated = true;
    }
  }

  // 9. Cache invalidation.
  invalidateCachePrefix("competitions:");
  invalidateCache("rankingCompetitions:all");
  invalidateCachePrefix("rankingSnapshot:");
  invalidateCachePrefix("clubRankingSnapshot:");
  invalidateCachePrefix("snapshotKeys:");

  return {
    rankingResultsUpdated:    rrDocs.length,
    registrationsUpdated:     regDocs.length,
    rankingCompetitionUpdated,
  };
};